import { useEffect, useRef, useState } from 'react'
import {
  Actor,
  ActorOptions,
  AnyActorLogic,
  createActor,
  IsNotNever,
  Observer,
  RequiredActorOptionsKeys,
  SnapshotFrom,
  toObserver,
} from 'xstate'

export const useActor = <TLogic extends AnyActorLogic>(
  actorLogic: TLogic,
  ...[options, observerOrListener]: IsNotNever<RequiredActorOptionsKeys<TLogic>> extends true
    ? [
        options: ActorOptions<TLogic> & {
          [K in RequiredActorOptionsKeys<TLogic>]: unknown
        },
        observerOrListener?: Observer<SnapshotFrom<TLogic>> | ((value: SnapshotFrom<TLogic>) => void),
      ]
    : [
        options?: ActorOptions<TLogic>,
        observerOrListener?: Observer<SnapshotFrom<TLogic>> | ((value: SnapshotFrom<TLogic>) => void),
      ]
): Actor<TLogic> => {
  const ignoreReactStrictModeEffect = useRef(process.env.NODE_ENV === 'development')
  const [actor] = useState(() => createActor(actorLogic, options))

  useEffect(() => {
    if (!observerOrListener) {
      return
    }

    const sub = actor.subscribe(toObserver(observerOrListener))
    return () => {
      sub.unsubscribe()
    }
  }, [observerOrListener, actor])

  useEffect(() => {
    actor.start()

    return () => {
      if (ignoreReactStrictModeEffect.current) {
        ignoreReactStrictModeEffect.current = false
        return
      }
      actor.stop()
    }
  }, [actor])

  return actor
}
